import { routes } from "@constants/routes";
import { ErrorPage, NotFoundPage, ViewportLoading } from "@cpj-ai/panel-npm-package";
import { lazy, ReactNode, Suspense } from "react";
import { LinkedInCallback } from "react-linkedin-login-oauth2";
import { createBrowserRouter, RouteObject, useNavigate } from "react-router-dom";

const ProtectedRoute = lazy(() => import("./ProtectedRoute"));

// Layouts
const MainLayout = lazy(() => import("@layouts/Main"));
const AuthLayout = lazy(() => import("@layouts/Auth"));

// Auth Pages
const LoginPage = lazy(() => import("@pages/Auth/Login"));
const SignUpPage = lazy(() => import("@pages/Auth/SignUp"));
const VerifyAccountPage = lazy(() => import("@pages/Auth/VerifyAccount"));
const SetPasswordCompletedPage = lazy(() => import("@pages/Auth/SetPasswordCompleted"));
const SetPasswordLinkSentPage = lazy(() => import("@pages/Auth/SetPasswordLinkSent"));
const SetPasswordPage = lazy(() => import("@pages/Auth/SetPassword"));
const ResetPasswordLinkSentPage = lazy(() => import("@pages/Auth/ResetPasswordLinkSent"));
const ResetPasswordPage = lazy(() => import("@pages/Auth/ResetPassword"));
const ResetPasswordCompletedPage = lazy(() => import("@pages/Auth/ResetPasswordCompleted"));
const SetNewPasswordPage = lazy(() => import("@pages/Auth/SetNewPassword"));

// Main Pages
const DashboardPage = lazy(() => import("@pages/Dashboard"));
const ResumePage = lazy(() => import("@pages/Resume"));
const JobInterestPage = lazy(() => import("@pages/JobInterest"));
const AssessmentsPage = lazy(() => import("@pages/Assessments"));
const AssessmentsListPage = lazy(() => import("@pages/Assessments/assessmentsList"));
const CollegePage = lazy(() => import("@pages/College"));
const JobSuggestionsPage = lazy(() => import("@pages/JobSuggestions"));
const JobSuggestionViewPage = lazy(() => import("@pages/JobSuggestions/view"));
const AIInterviewPage = lazy(() => import("@pages/AIInterview"));
const SettingsPage = lazy(() => import("@pages/Settings"));
const PostHiringPage = lazy(() => import("@pages/PostHiring"));
const SupportPage = lazy(() => import("@pages/Support"));

// Add the import for the new Onboarding page
const OnboardingPage = lazy(() => import("@modules/Onboarding"));

type RouteConfig = {
  path: string;
  element: ReactNode;
  children?: RouteConfig[];
  isProtected?: boolean;
  errorElement?: ReactNode;
  hasErrorBoundary?: boolean;
};

const withSuspense = (Component: React.ComponentType) => (
  <Suspense fallback={<ViewportLoading />}>
    <Component />
  </Suspense>
);

const createRoutes = (routes: RouteConfig[]): RouteObject[] =>
  routes.map(({ path, element, children, isProtected, errorElement, hasErrorBoundary }) => ({
    path,
    element: withSuspense(() => (isProtected ? <ProtectedRoute>{element}</ProtectedRoute> : element)),
    children: children && createRoutes(children),
    errorElement:
      !import.meta.env.VITE_DEBUG_MODE && (hasErrorBoundary || errorElement)
        ? withSuspense(() => errorElement ?? <ErrorPage />)
        : undefined,
  }));

const authRoutes: RouteConfig[] = [
  { path: "", element: <LoginPage /> },
  { path: routes.auth.signUp, element: <SignUpPage /> },
  { path: routes.auth.setPassword.linkSent, element: <SetPasswordLinkSentPage /> },
  { path: routes.auth.setPassword.setPassword, element: <SetPasswordPage /> },
  { path: routes.auth.setPassword.completed, element: <SetPasswordCompletedPage /> },
  { path: routes.auth.resetPassword.main, element: <ResetPasswordPage /> },
  { path: routes.auth.resetPassword.linkSent, element: <ResetPasswordLinkSentPage /> },
  { path: routes.auth.resetPassword.setPassword, element: <SetNewPasswordPage /> },
  { path: routes.auth.resetPassword.completed, element: <ResetPasswordCompletedPage /> },
  { path: routes.auth.linkedIn, element: <LinkedInCallback /> },
];

const mainRoutes: RouteConfig[] = [
  { path: routes.dashboard.main, element: <DashboardPage />, isProtected: true },
  { path: routes.resume.main, element: <ResumePage />, isProtected: true },
  { path: routes.jobInterest.main, element: <JobInterestPage />, isProtected: true },
  { path: routes.assessment.main, element: <AssessmentsPage />, isProtected: true },
  { path: routes.assessment.list, element: <AssessmentsListPage />, isProtected: true },
  { path: routes.college.main, element: <CollegePage />, isProtected: true },
  { path: routes.jobSuggestions.main, element: <JobSuggestionsPage />, isProtected: true },
  { path: routes.jobSuggestions.view, element: <JobSuggestionViewPage />, isProtected: true },
  { path: routes.aiInterview.main, element: <AIInterviewPage />, isProtected: true },
  { path: routes.setting.main, element: <SettingsPage />, isProtected: true },
  { path: routes.postHiring.main, element: <PostHiringPage />, isProtected: true },
  { path: routes.support.main, element: <SupportPage />, isProtected: true },
];

const rootRoutes: RouteConfig[] = [
  {
    path: routes.auth.main,
    element: <AuthLayout />,
    children: authRoutes,
    hasErrorBoundary: true,
  },
  {
    path: routes.auth.verifyAccount,
    element: <VerifyAccountPage />,
    hasErrorBoundary: true,
  },
  {
    path: routes.onboarding.main,
    element: <OnboardingPage />,
    isProtected: false,
    hasErrorBoundary: true,
  },
  {
    path: "/",
    element: <MainLayout />,
    children: mainRoutes,
    hasErrorBoundary: true,
  },
  {
    path: routes.notFound,
    element: <NotFoundPage useNavigate={useNavigate} url={routes.dashboard.main} />,
    hasErrorBoundary: true,
  },
];

const MainRoutes = createBrowserRouter(createRoutes(rootRoutes));

export default MainRoutes;
